import { useState, useEffect, useCallback, useRef } from "react";
import {
  buildCityListParams,
  getCities,
  getCityMasterCountries,
  getCityMasterStates,
  getFilterVenues,
} from "../../../api/superAdminApi";
import {
  VM_transformCountryList,
  VM_transformStatesList,
  VM_transformCountryCitiesList,
} from "../../../Helper/RegistrationHelper";

/**
 * Super Admin cascade: Country → State → City → Venue (optional).
 * Uses /v1/superadmin/cities/* and /v1/superadmin/filters/venues.
 */
export function useLocationCascading({ includeVenue = true } = {}) {
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [venueOptions, setVenueOptions] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [venueId, setVenueId] = useState("");
  const [countryLabel, setCountryLabel] = useState("");
  const [stateLabel, setStateLabel] = useState("");
  const [cityLabel, setCityLabel] = useState("");
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingVenues, setLoadingVenues] = useState(false);

  const countryIdRef = useRef("");
  const stateIdRef = useRef("");

  const loadStates = useCallback(async (cid) => {
    if (!cid) {
      setStateOptions([]);
      return;
    }
    setLoadingStates(true);
    try {
      const res = await getCityMasterStates(cid);
      if (res.data?.status_code === 200 && Array.isArray(res.data.data)) {
        setStateOptions(VM_transformStatesList(res.data.data));
      } else {
        setStateOptions([]);
      }
    } catch (e) {
      console.error("superAdmin states", e);
      setStateOptions([]);
    } finally {
      setLoadingStates(false);
    }
  }, []);

  const loadCities = useCallback(async (cid, sid) => {
    if (!cid || !sid) {
      setCityOptions([]);
      return;
    }
    setLoadingCities(true);
    try {
      const res = await getCities(
        buildCityListParams({ page: 0, pageSize: 5000, countryId: cid, stateId: sid })
      );
      if (res.data?.status_code === 200 && Array.isArray(res.data.data)) {
        const raw = VM_transformCountryCitiesList(res.data.data);
        setCityOptions(raw.filter((c) => c.label && String(c.label).trim() !== ""));
      } else {
        setCityOptions([]);
      }
    } catch (e) {
      console.error("superAdmin cities", e);
      setCityOptions([]);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  const loadVenues = useCallback(
    async (cid, sid, city, search = "") => {
      if (!includeVenue) {
        setVenueOptions([]);
        return;
      }
      if (!city) {
        setVenueOptions([]);
        return;
      }
      setLoadingVenues(true);
      try {
        const params = { country: cid, state: sid, city };
        if (search.trim()) params.search = search.trim();
        const res = await getFilterVenues(params);
        const list = res.data?.data;
        if (Array.isArray(list)) {
          setVenueOptions(
            list.map((v) => ({
              id: v.venue_id,
              value: v.venue_id,
              label: v.venue_name || v.venue_id,
              venue: v,
            }))
          );
        } else {
          setVenueOptions([]);
        }
      } catch (e) {
        console.error("superAdmin filter venues", e);
        setVenueOptions([]);
      } finally {
        setLoadingVenues(false);
      }
    },
    [includeVenue]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCityMasterCountries();
        if (cancelled) return;
        if (res.data?.status_code === 200 && Array.isArray(res.data.data)) {
          setCountryOptions(VM_transformCountryList(res.data.data));
        }
      } catch (e) {
        console.error("superAdmin countries", e);
      } finally {
        if (!cancelled) setLoadingCountries(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resetVenue = useCallback(() => {
    setVenueId("");
    setVenueOptions([]);
  }, []);

  const onCountryChange = useCallback(
    (value) => {
      const next = value || "";
      setCountryId(next);
      countryIdRef.current = next;
      const opt = countryOptions.find((o) => String(o.value) === String(next));
      setCountryLabel(opt?.label || "");
      setStateId("");
      setCityId("");
      stateIdRef.current = "";
      setStateLabel("");
      setCityLabel("");
      setStateOptions([]);
      setCityOptions([]);
      resetVenue();
      loadStates(next);
    },
    [loadStates, countryOptions, resetVenue]
  );

  const onStateChange = useCallback(
    (value) => {
      const next = value || "";
      setStateId(next);
      stateIdRef.current = next;
      const opt = stateOptions.find((o) => String(o.value) === String(next));
      setStateLabel(opt?.label || "");
      setCityId("");
      setCityLabel("");
      setCityOptions([]);
      resetVenue();
      loadCities(countryIdRef.current, next);
    },
    [loadCities, stateOptions, resetVenue]
  );

  const onCityChange = useCallback(
    (value) => {
      const next = value || "";
      setCityId(next);
      const opt = cityOptions.find((o) => String(o.value) === String(next));
      setCityLabel(opt?.label || "");
      setVenueId("");
      setVenueOptions([]);
      if (next) {
        loadVenues(countryIdRef.current, stateIdRef.current, next);
      }
    },
    [cityOptions, loadVenues]
  );

  const onVenueChange = useCallback((value) => {
    setVenueId(value || "");
  }, []);

  const resetLocation = useCallback(() => {
    setCountryId("");
    setStateId("");
    setCityId("");
    countryIdRef.current = "";
    stateIdRef.current = "";
    setCountryLabel("");
    setStateLabel("");
    setCityLabel("");
    setStateOptions([]);
    setCityOptions([]);
    resetVenue();
  }, [resetVenue]);

  return {
    countryOptions,
    stateOptions,
    cityOptions,
    venueOptions,
    countryId,
    stateId,
    cityId,
    venueId,
    countryLabel,
    stateLabel,
    cityLabel,
    onCountryChange,
    onStateChange,
    onCityChange,
    onVenueChange,
    resetLocation,
    loadingCountries,
    loadingStates,
    loadingCities,
    loadingVenues,
    includeVenue,
  };
}
